import React from 'react';
import type { ReactElement } from 'react';
import { FilterPillButton } from './FilterPillButton';
import { PriceFilter } from './PriceFilter';
import type { AllChannelObjects } from '../../../../shared/model/tsTypes';
import { FilterPillButtonSheet } from './FilterPillButtonSheet';
import useMedia from '../../../../shared/useMedia';

export interface FilterPillPriceProps {
  selectedChannel: AllChannelObjects;
  selectedPriceRange: number[];
  initialPriceRange: number[];
  onChange: (newPriceRange: number[]) => void;
  onFilterPillClose: () => void;
  onReset?: () => void;
  handleSearchOnClick: (onClose?: () => void) => void;
  trackPillClick: () => void;
}

const formatToShortPrice = (number: number): string =>
  new Intl.NumberFormat('en-AU', {
    maximumSignificantDigits: 3,
    notation: 'compact',
    compactDisplay: 'short',
    style: 'currency',
    currency: 'AUD',
  })
    .format(number)
    .toLowerCase();

const getPillLabel = (priceRange: number[]): string | undefined => {
  const [minPrice, maxPrice] = priceRange;
  const hasMinPrice = minPrice > 0;
  const hasMaxPrice = maxPrice > 0;

  if (!hasMinPrice && !hasMaxPrice) {
    return undefined;
  }

  const minPriceText = formatToShortPrice(minPrice);
  const maxPriceText = formatToShortPrice(maxPrice);

  if (!hasMinPrice) {
    return `Up to ${maxPriceText}`;
  }

  if (!hasMaxPrice) {
    return `${minPriceText}+`;
  }

  return `${minPriceText}-${maxPriceText}`;
};

export const FilterPillPrice = ({
  selectedChannel,
  selectedPriceRange,
  initialPriceRange,
  onChange,
  onReset,
  onFilterPillClose,
  handleSearchOnClick,
  trackPillClick,
}: FilterPillPriceProps): ReactElement => {
  const maybePillLabel = getPillLabel(initialPriceRange);
  const { isMediumScreen, isLargeScreen } = useMedia();

  return !isMediumScreen && !isLargeScreen ? (
    <FilterPillButtonSheet
      buttonLabel={maybePillLabel ?? 'Price'}
      sheetLabel="Price"
      selected={!!maybePillLabel}
      onReset={onReset}
      onFilterPillClose={onFilterPillClose}
      handleSearchOnClick={handleSearchOnClick}
      onFilterPillOpen={() => {
        trackPillClick();
      }}
      filter={
        <PriceFilter
          selectedChannel={selectedChannel}
          searchQueryPriceRange={selectedPriceRange}
          onChange={onChange}
          showDivider={false}
          hideHeading
        />
      }
    />
  ) : (
    <FilterPillButton
      label={maybePillLabel ?? 'Price'}
      selected={!!maybePillLabel}
      onReset={onReset}
      onFilterPillClose={onFilterPillClose}
      handleSearchOnClick={handleSearchOnClick}
      onFilterPillOpen={() => {
        trackPillClick();
      }}
    >
      <PriceFilter
        selectedChannel={selectedChannel}
        searchQueryPriceRange={selectedPriceRange}
        onChange={onChange}
        showDivider={false}
      />
    </FilterPillButton>
  );
};
